import { ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

import messages from "../messages/ja.json";
import { Icon } from "./icon";

import "../styles/app-dialog.css";

interface Props {
  open: boolean;
  title: string;
  // Body copy. Newlines are kept as-is (white-space: pre-line), so the ja.json strings
  // keep the wrapping the design asks for without any markup here.
  message?: string;
  // Richer body when a plain string isn't enough. Ignored while `message` is set.
  children?: ReactNode;
  // Footer button label. Defaults to 閉じる.
  actionLabel?: string;
  // "alertdialog" for errors (assistive tech announces them straight away), "dialog" for a
  // plain confirm/info modal.
  role?: "dialog" | "alertdialog";
  // Fired by the footer button, the ✕, Escape, and a click outside the card.
  onClose: () => void;
}

// Everything focusable the dialog itself can contain today (✕ + footer action). Kept as one
// selector so the Tab trap below never has to know which of them is rendered.
const FOCUSABLE = 'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

// Shared modal dialog (Figma PC: 735px card, 24px gutters, one full-width footer button).
// Screen-agnostic on purpose: the face-registration and verify-mfa flows keep their own
// CSS namespaces, so the dialog carries its own (.app-dialog-*) and can be dropped into
// either without touching them.
//
// Rendered through a portal on <body> so the app root can be marked `inert` while it is
// open — otherwise the screen behind stays operable (on the face screens, tabbing to the
// "tap to start camera" overlay and pressing Enter restarted the camera under the dialog).
export function AppDialog({
  open,
  title,
  message,
  children,
  actionLabel = messages.dialog.close,
  role = "dialog",
  onClose,
}: Props) {
  const cardRef = useRef<HTMLDivElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);
  // What had focus before the dialog opened, so it can be handed back on close.
  const restoreFocusRef = useRef<HTMLElement | null>(null);
  const titleId = useId();
  const bodyId = useId();

  // Escape closes, same as the ✕ and the footer button.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  // Take the rest of the app out of the tab order / accessibility tree while the dialog is
  // up. The dialog itself is portalled outside #root, so it stays reachable.
  // Declared BEFORE the focus effect on purpose: cleanups run in declaration order, so the
  // app is interactive again by the time focus is handed back into it.
  useEffect(() => {
    if (!open) return;
    const root = document.getElementById("root");
    root?.setAttribute("inert", "");
    return () => root?.removeAttribute("inert");
  }, [open]);

  // Move focus onto the only action so the keyboard lands inside the dialog, and give it
  // back to the opener (if it is still on screen) when the dialog goes away.
  useEffect(() => {
    if (!open) return;
    restoreFocusRef.current = document.activeElement as HTMLElement | null;
    actionRef.current?.focus();
    return () => {
      const opener = restoreFocusRef.current;
      if (opener && document.contains(opener)) opener.focus();
    };
  }, [open]);

  // Freeze the page behind the dialog: on mobile a scroll started on the backdrop would
  // otherwise drag the screen underneath around while the error is up.
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  if (!open) return null;

  // Tab trap: `inert` already blocks the app behind, but browser chrome (and older Safari)
  // can still walk out of the card, so wrap the tab order inside it.
  const onCardKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Tab") return;
    const focusables = cardRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE);
    if (!focusables || focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return createPortal(
    <div
      className="app-dialog-backdrop"
      data-testid="app-dialog-backdrop"
      // mousedown (not click): a drag that starts inside the card and ends on the
      // backdrop would otherwise close the dialog.
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className="app-dialog"
        ref={cardRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        onKeyDown={onCardKeyDown}
      >
        <header className="app-dialog-header">
          <h2 className="app-dialog-title" id={titleId}>
            {title}
          </h2>
          <button
            className="app-dialog-close"
            type="button"
            // Distinct from the footer button so screen-reader users can tell the two
            // apart in a controls list.
            aria-label={messages.dialog.closeDialog}
            onClick={onClose}
          >
            <Icon name="close" size={14} />
          </button>
        </header>

        <div className="app-dialog-body" id={bodyId}>
          {message ? <p className="app-dialog-message">{message}</p> : children}
        </div>

        <footer className="app-dialog-footer">
          <button
            ref={actionRef}
            className="app-dialog-action"
            type="button"
            onClick={onClose}
          >
            {actionLabel}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
}

// Error flavour of the dialog (エラー title, announced as an alert). The screens only ever
// need this one, but the shell above stays generic so a confirm/info modal can reuse it.
export function ErrorDialog({
  title = messages.dialog.errorTitle,
  ...props
}: Omit<Props, "title" | "role"> & { title?: string }) {
  return <AppDialog title={title} role="alertdialog" {...props} />;
}
